import {type FormEvent, type ReactNode, useState} from "react";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  CheckIcon,
  CopyIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react";
import {toast} from "sonner";

import {type CreateLairOutputBody, type Lair, lairsCreate, lairsDelete, lairsList, lairsUpdate} from "@/sdk";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {useAuth} from "@/lib/auth";
import {formatRelative} from "@/lib/format";

const QK = ["admin", "lairs"] as const;

type LairForm = {
  name: string;
  hostname: string;
  hypervisor_kind: string;
  hypervisor_version: string;
  organization_id: string;
};

const emptyForm: LairForm = {
  name: "",
  hostname: "",
  hypervisor_kind: "kvm",
  hypervisor_version: "",
  organization_id: "",
};

export const AdminLairs = () => {
  const qc = useQueryClient();
  const {principal} = useAuth();
  // For admins the MeProfile.account doesn't carry an org. v0: admins must
  // type the organization_id when creating a lair (lairs are scoped per-org).
  // TODO: surface an org-picker once an /organizations endpoint exists.
  const defaultOrg = principal?.user?.organization_id ?? "";

  const [openForm, setOpenForm] = useState<{mode: "create" | "edit"; lair?: Lair} | null>(null);
  const [form, setForm] = useState<LairForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Lair | null>(null);
  // bootstrap holds the one-time response from POST /lairs (plaintext
  // org_key + org_secret + the copy-paste install snippet). null when
  // closed; cleared on close so React drops the plaintext from memory.
  const [bootstrap, setBootstrap] = useState<CreateLairOutputBody | null>(null);

  const query = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const {data, error} = await lairsList();
      if (error) throw error;
      return data?.items ?? [];
    },
  });

  const invalidate = () => qc.invalidateQueries({queryKey: QK});

  const createM = useMutation({
    mutationFn: () =>
      lairsCreate({
        body: {
          name: form.name,
          hostname: form.hostname || undefined,
          hypervisor_kind: form.hypervisor_kind || undefined,
          hypervisor_version: form.hypervisor_version || undefined,
          organization_id: form.organization_id,
        },
      }),
    onSuccess: ({data, error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      // The response carries the lair plus one-time bootstrap creds + an
      // operator install snippet. Stash it in state to show the dialog;
      // the plaintext disappears from memory when the operator dismisses.
      setOpenForm(null);
      if (data) setBootstrap(data);
      toast.success("Lair created — capture the bootstrap creds now");
      invalidate();
    },
  });

  const updateM = useMutation({
    mutationFn: () =>
      lairsUpdate({
        path: {id: openForm!.lair!.id},
        body: {
          name: form.name || undefined,
          hostname: form.hostname || undefined,
          hypervisor_kind: form.hypervisor_kind || undefined,
          hypervisor_version: form.hypervisor_version || undefined,
        },
      }),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("Lair updated");
      setOpenForm(null);
      invalidate();
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => lairsDelete({path: {id}}),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("Lair deleted");
      setDeleteTarget(null);
      invalidate();
    },
  });

  const startCreate = () => {
    setForm({...emptyForm, organization_id: defaultOrg});
    setOpenForm({mode: "create"});
  };

  const startEdit = (lair: Lair) => {
    setForm({
      name: lair.name ?? "",
      hostname: lair.hostname ?? "",
      hypervisor_kind: lair.hypervisor_kind ?? "kvm",
      hypervisor_version: lair.hypervisor_version ?? "",
      organization_id: lair.organization_id,
    });
    setOpenForm({mode: "edit", lair});
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (openForm?.mode === "create") createM.mutate();
    else updateM.mutate();
  };

  const lairs = query.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Lairs</h1>
          <p className="text-sm text-muted-foreground">
            Hypervisor hosts available to customer servers.
          </p>
        </div>
        <Button onClick={startCreate}>
          <PlusIcon /> Add lair
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              <Loader2Icon className="size-5 animate-spin" />
            </div>
          ) : query.isError ? (
            <div className="p-6 text-sm text-destructive">Failed to load lairs.</div>
          ) : lairs.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No lairs registered.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name / Hostname</TableHead>
                  <TableHead>Hypervisor</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lairs.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>
                      <div className="font-medium">{l.name}</div>
                      <div className="text-xs text-muted-foreground">{l.hostname}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="muted">
                        {l.hypervisor_kind || "—"}
                        {l.hypervisor_version ? ` ${l.hypervisor_version}` : ""}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelative(l.last_seen_at)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{l.organization_id}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <MoreHorizontalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => startEdit(l)}>
                            <PencilIcon /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setDeleteTarget(l)}
                          >
                            <Trash2Icon /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={openForm !== null} onOpenChange={(open) => !open && setOpenForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {openForm?.mode === "create" ? "Add lair" : "Edit lair"}
            </DialogTitle>
            <DialogDescription>
              Hypervisor host metadata. Agents enroll separately and update last-seen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormRow id="name" label="Name">
              <Input
                id="name"
                required
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
              />
            </FormRow>
            <FormRow id="hostname" label="Hostname">
              <Input
                id="hostname"
                placeholder="hv01.example.com"
                value={form.hostname}
                onChange={(e) => setForm({...form, hostname: e.target.value})}
              />
            </FormRow>
            <div className="grid grid-cols-2 gap-3">
              <FormRow id="kind" label="Hypervisor kind">
                <Input
                  id="kind"
                  placeholder="kvm"
                  value={form.hypervisor_kind}
                  onChange={(e) => setForm({...form, hypervisor_kind: e.target.value})}
                />
              </FormRow>
              <FormRow id="version" label="Version">
                <Input
                  id="version"
                  placeholder="9.0"
                  value={form.hypervisor_version}
                  onChange={(e) => setForm({...form, hypervisor_version: e.target.value})}
                />
              </FormRow>
            </div>
            {openForm?.mode === "create" ? (
              <FormRow id="org" label="Organization ID">
                <Input
                  id="org"
                  required
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={form.organization_id}
                  onChange={(e) => setForm({...form, organization_id: e.target.value})}
                />
              </FormRow>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createM.isPending || updateM.isPending}
              >
                {openForm?.mode === "create"
                  ? createM.isPending
                    ? "Creating…"
                    : "Create lair"
                  : updateM.isPending
                    ? "Saving…"
                    : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete lair?</DialogTitle>
            <DialogDescription>
              Soft-deletes <span className="font-mono">{deleteTarget?.name}</span>. Servers
              already pinned to it will become unschedulable on this host.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteM.isPending}
              onClick={() => deleteTarget && deleteM.mutate(deleteTarget.id)}
            >
              {deleteM.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BootstrapDialog
        bootstrap={bootstrap}
        onClose={() => setBootstrap(null)}
      />
    </div>
  );
};

function BootstrapDialog({
  bootstrap,
  onClose,
}: {
  bootstrap: CreateLairOutputBody | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<"snippet" | "key" | "secret" | null>(null);

  const copy = async (kind: "snippet" | "key" | "secret", text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      toast.success(`${kind === "snippet" ? "Install snippet" : kind === "key" ? "Org key" : "Org secret"} copied`);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  return (
    <Dialog open={bootstrap !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Onboard {bootstrap?.lair.name ?? "this hypervisor"}</DialogTitle>
          <DialogDescription>
            Bootstrap credentials are returned ONCE. Capture the install snippet now —
            losing it means revoking and re-issuing the lair.
          </DialogDescription>
        </DialogHeader>

        {bootstrap ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
              <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
              <span>
                Run the snippet on the hypervisor host as root. It writes
                <code className="mx-1 font-mono">/var/lib/eyrie-agent/state.json</code>,
                downloads the agent binary, and starts the agent in auto-bootstrap
                mode (apt-installs qemu/libvirt on debian/ubuntu).
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Install snippet</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copy("snippet", bootstrap.operator_snippet)}
                >
                  {copied === "snippet" ? <CheckIcon /> : <CopyIcon />}
                  {copied === "snippet" ? "Copied" : "Copy"}
                </Button>
              </div>
              <pre className="max-h-64 overflow-auto rounded-md border bg-muted px-3 py-2 font-mono text-xs">
                {bootstrap.operator_snippet}
              </pre>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Org key (visible later)</Label>
                <div className="flex items-stretch gap-2">
                  <code className="flex-1 break-all rounded-md border bg-muted px-2 py-1.5 font-mono text-xs">
                    {bootstrap.org_key}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copy("key", bootstrap.org_key)}
                  >
                    {copied === "key" ? <CheckIcon /> : <CopyIcon />}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Org secret (one-time)</Label>
                <div className="flex items-stretch gap-2">
                  <code className="flex-1 break-all rounded-md border bg-muted px-2 py-1.5 font-mono text-xs">
                    {bootstrap.org_secret}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copy("secret", bootstrap.org_secret)}
                  >
                    {copied === "secret" ? <CheckIcon /> : <CopyIcon />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button onClick={onClose}>I have captured the credentials</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormRow({id, label, children}: {id: string; label: string; children: ReactNode}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

function extractError(err: unknown): string {
  return (err as {detail?: string} | undefined)?.detail ?? "Request failed";
}
